import { useMemo, useState } from 'react';
import { IconAlertTriangle, IconBuilding, IconCoin, IconCopy, IconLock, IconPhone, IconPlus, IconSearch, IconShieldCheck, IconSparkles, IconUserShare, IconUsers, IconClock, IconChevronLeft, IconChevronRight, IconSignature, IconPlayerPause, IconBan } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { DataError } from '@hana/data';
import { useAllCustomers } from './useAllCustomers';
import { HeaderTools, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Segmented, StateView, Skeleton, useToast, toggleAll, toggleOne, type ScreenState } from '@hana/ui';
import { searchCustomers } from './logic';
import { abbr, attention, holdsDo, initials, limitState, myChip, shortDate } from './view';
import type { Customer } from './types';
import { CustomerCompact } from './CustomerCompact';
import { CustomersDock, useCustomersDock } from './CustomersDock';
import { useT } from '@hana/i18n';
import './i18n';
import s from './customers.module.css';

// Mint-Customers (desktop): tab · tìm + Hỏi Hana + Lọc · bảng 8 cột. Chỉ đọc; nút ghi báo toast.
// Người xem chưa có quyền `money` (mặc định): cột tiền chỉ hiện trạng thái, không số tiền (đúng thanh "Số tiền ẩn" của board).

const TABS = [
  { key: 'cus', label: 'crm.cu.tab.cus' },
  { key: 'lead', label: 'crm.cu.tab.lead', n: 8 },
  { key: 'partner', label: 'crm.cu.tab.partner', n: 8 },
  { key: 'hq', label: 'crm.cu.tab.hq' },
] as const;
type Tab = (typeof TABS)[number]['key'];

/** Rộng cột theo board (CW 1240): chọn 46 · khách 242 · sale 168 · BKG 140 · job 100 · hạn mức 182 · quá hạn 100 · myHANA 142 · cập nhật 120. */
const COLS = [
  { key: 'name', header: 'crm.cu.col.name', w: 242 },
  { key: 'sale', header: 'crm.cu.col.sale', w: 168 },
  { key: 'bkg', header: 'crm.cu.col.bkg', w: 140 },
  { key: 'jobs', header: 'crm.cu.col.jobs', w: 100, right: true },
  { key: 'lim', header: 'crm.cu.col.lim', w: 182 },
  { key: 'od', header: 'crm.cu.col.od', w: 100, right: true },
  { key: 'my', header: 'crm.cu.col.my', w: 142 },
  { key: 'upd', header: 'crm.cu.col.upd', w: 120 },
] as const;
const TOTAL = 46 + COLS.reduce((a, c) => a + c.w, 0);
const pct = (w: number) => `${((w / TOTAL) * 100).toFixed(3)}%`;

const Avatar = ({ text }: { text: string }) => (
  <span className={s.ab} aria-hidden="true">
    {text}
  </span>
);

function Flags({ c }: { c: Customer }) {
  const t = useT();
  const st = c.status !== 'ap' ? t(`crm.cu.status.${c.status}`) : '';
  const ct = c.contract !== 'sign' && c.jobs > 0 ? t(`crm.cu.contract.${c.contract}`) : '';
  const StIcon = c.status === 'bl' ? IconBan : c.status === 'inact' ? IconPlayerPause : IconClock;
  return (
    <>
      {st && (
        <span role="img" title={st} aria-label={st} className={`${s.flag} ${c.status === 'bl' ? s.err : s.warn}`}>
          <StIcon size={13} stroke={1.8} aria-hidden="true" />
        </span>
      )}
      {ct && (
        <span role="img" title={ct} aria-label={ct} className={`${s.flag} ${c.contract === 'unsigned' ? s.lb : s.warn}`}>
          <IconSignature size={13} stroke={1.8} aria-hidden="true" />
        </span>
      )}
    </>
  );
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

/** Máy bàn: bảng (Mint-Customers). iPad / điện thoại: danh bạ dạng thẻ (Mint-Customers-iPad / -Phone). */
export function CustomerList() {
  const layout = useLayout();
  return layout === 'pad' || layout === 'phone' ? <CustomerCompact phone={layout === 'phone'} /> : <CustomerTable />;
}

function CustomerTable() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { data, error, isLoading, isError, refetch } = useAllCustomers();
  const dock = useCustomersDock();
  const forced = readState();
  const [tab, setTab] = useState<Tab>('cus');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const all = data?.items ?? [];
  const rows = useMemo(() => searchCustomers(all, q), [all, q]);
  const keys = rows.map((r) => r.id);
  const nAttention = all.filter(attention).length;

  const noWrite = () => toast({ msg: t('crm.noWrite') });
  const pickTab = (k: Tab) => (k === 'cus' ? setTab(k) : toast({ msg: t('crm.cu.tabLater') }));

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const ready = state === 'ready';

  const tabs = TABS.map((x) => ({ key: x.key, label: t(x.label), n: 'n' in x ? x.n : x.key === 'cus' && ready ? all.length : undefined }));

  return (
    <Page kind="table" dock={dock.wide && ready ? <CustomersDock rows={all} dock={dock} /> : undefined}>
      <PageHeader
        title={t('crm.cu.title')}
        tabs={<Segmented label={t('crm.cu.group')} value={tab} onChange={pickTab} items={tabs} />}
        actions={
          <>
            <HeaderTools tasks={5} mail={3} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {t('crm.cu.add')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tb}>
          <label className={s.search}>
            <IconSearch size={16} aria-hidden="true" />
            <input aria-label={t('crm.cu.search')} placeholder={t('crm.cu.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
            <button type="button" className={s.hana} title={t('crm.cu.hanaTitle')} aria-label={t('crm.cu.hanaTitle')} onClick={() => toast({ msg: t('crm.cu.hanaLater') })}>
              <IconSparkles size={13} stroke={2} aria-hidden="true" />
              {t('crm.cu.hana')}
            </button>
          </label>
          <button type="button" className={s.filter} onClick={() => toast({ msg: t('crm.cu.filterLater') })}>
            <IconPlus size={16} aria-hidden="true" />
            {t('crm.cu.filter')}
          </button>
          {ready && (
            <div className={s.sum}>
              <span title={t('crm.cu.shown')}>
                <IconBuilding size={14} aria-hidden="true" />
                {t('crm.cu.count', { n: rows.length })}
              </span>
              {nAttention > 0 && (
                <span className={s.sumHot} title={t('crm.cu.attention')}>
                  <IconAlertTriangle size={14} aria-hidden="true" />
                  {t('crm.cu.count', { n: nAttention })}
                </span>
              )}
            </div>
          )}
        </div>
        <StateView
          state={ready && rows.length === 0 ? 'empty' : state}
          title={ready ? t('crm.cu.noMatch') : state === 'empty' ? t('crm.cu.empty') : undefined}
          desc={ready ? t('crm.cu.noMatchDesc') : undefined}
          icon={ready || state === 'empty' ? <IconUsers size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('crm.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          <section className={s.card} aria-label={t('crm.cu.list')}>
            <div className={s.lock}>
              <IconLock size={14} aria-hidden="true" />
              {t('crm.cu.hidden')} ••••
            </div>
            <table className={`${s.table} ${selected.size ? s.picking : ''}`} aria-label={t('crm.cu.title')} aria-rowcount={rows.length + 1}>
              <colgroup>
                <col style={{ width: pct(46) }} />
                {COLS.map((c) => (
                  <col key={c.key} style={{ width: pct(c.w) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className={s.selCell}>
                    <input type="checkbox" className={s.check} aria-label={t('crm.cu.selectAll')} checked={keys.length > 0 && selected.size === keys.length} onChange={() => setSelected(toggleAll(selected, keys))} />
                  </th>
                  {COLS.map((c) => (
                    <th key={c.key} scope="col" className={'right' in c ? s.right : undefined}>
                      {t(c.header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c, i) => {
                  const on = selected.has(c.id);
                  const lim = limitState(c);
                  const my = myChip(c.my);
                  const od = c.credit.overdue > 0;
                  return (
                    <tr key={c.id} className={on ? s.rowOn : undefined} aria-rowindex={i + 2} aria-selected={on}>
                      <td className={s.selCell}>
                        <input type="checkbox" className={s.check} aria-label={t('crm.cu.select', { id: c.id })} checked={on} onChange={() => setSelected(toggleOne(selected, c.id))} />
                      </td>
                      <td>
                        <div className={s.who}>
                          <span className={s.sq} aria-hidden="true">
                            {initials(c.id)}
                          </span>
                          <div className={s.whoTx}>
                            <div className={s.nameRow}>
                              <button type="button" className={s.name} onClick={() => void navigate({ to: '/ezy/customers/$customerId', params: { customerId: c.id } })}>
                                {c.id}
                              </button>
                              <button type="button" className={s.mini} title={t('crm.cu.copySn')} aria-label={t('crm.cu.copySnAria', { id: c.id })} onClick={() => toast({ msg: t('crm.cu.copied', { id: c.id }) })}>
                                <IconCopy size={13} aria-hidden="true" />
                              </button>
                              {c.contactCount > 0 && (
                                <button type="button" className={s.mini} title={t('crm.cu.call')} aria-label={t('crm.cu.callAria', { id: c.id })} onClick={noWrite}>
                                  <IconPhone size={13} aria-hidden="true" />
                                </button>
                              )}
                            </div>
                            <div className={s.sub}>
                              MST {c.mst}
                              <Flags c={c} />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={s.person} title={t('crm.cu.saleOf', { v: c.sale })}>
                          <Avatar text={abbr(c.sale)} />
                          <span>{c.sale}</span>
                        </span>
                        {c.split && <div className={s.sub}>{c.split}</div>}
                      </td>
                      <td>
                        <span className={s.person} title={t('crm.cu.bkgOf', { v: c.bkg })}>
                          <Avatar text={abbr(c.bkg)} />
                          <span>{c.bkg}</span>
                        </span>
                        <div className={s.sub} title={t('crm.cu.bkg2Of', { v: c.bkg2 })}>
                          <IconUserShare size={13} stroke={1.8} aria-hidden="true" />
                          {c.bkg2}
                        </div>
                      </td>
                      <td className={`${s.right} ${s.jobs}`}>{c.jobs}</td>
                      <td>
                        <span className={`${s.limit} ${lim === 'over' ? s.err : ''}`} title={`${t('crm.cu.limit')} · ${t(`crm.cu.lim.${lim}`)}`}>
                          {lim === 'prepaid' ? <IconCoin size={14} stroke={1.8} aria-hidden="true" /> : lim === 'over' ? <IconAlertTriangle size={14} stroke={1.8} aria-hidden="true" /> : <IconShieldCheck size={14} stroke={1.8} aria-hidden="true" />}
                          {t(`crm.cu.lim.${lim}`)}
                        </span>
                      </td>
                      <td className={s.right}>
                        <b className={`${s.od} ${od ? s.err : s.lb}`} title={`${t('crm.cu.debt')} · ${od ? t('crm.cu.overdue') : '—'}`}>
                          {od && <IconAlertTriangle size={13} stroke={1.8} aria-hidden="true" />}
                          {od ? t('crm.cu.overdue') : '—'}
                        </b>
                        {holdsDo(c) && (
                          <div className={`${s.hold} ${s.err}`} title={t('crm.cu.holdDo')}>
                            <IconLock size={13} stroke={1.8} aria-hidden="true" />
                            DO
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`${s.my} ${s[`my_${my.tone}`]}`}>
                          <span className={s.dot} aria-hidden="true" />
                          <span title={`myHANA · ${my.text}`} className={s.myTx}>
                            {my.tone === 'ok' && <IconUsers size={13} stroke={1.8} aria-hidden="true" />}
                            {my.tone === 'warn' && <IconClock size={13} stroke={1.8} aria-hidden="true" />}
                            {my.text}
                          </span>
                        </span>
                      </td>
                      <td title={t('crm.cu.updatedBy', { d: c.updated, u: c.updatedBy })} className={s.upd}>
                        <span className={s.person}>
                          <Avatar text={abbr(c.updatedBy)} />
                          {shortDate(c.updated)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={s.foot}>
              <span>
                {t('crm.cu.countOf', { n: rows.length, total: all.length })}
              </span>
              <div className={s.pager}>
                <button type="button" className={s.pg} aria-label={t('crm.cu.prev')} disabled>
                  <IconChevronLeft size={14} aria-hidden="true" />
                </button>
                <button type="button" className={s.pg} aria-label={t('crm.cu.next')} disabled>
                  <IconChevronRight size={14} aria-hidden="true" />
                </button>
              </div>
            </div>
          </section>
        </StateView>
      </div>
    </Page>
  );
}
